import React from 'react';
import { Typography, Row, Col, Card } from 'antd';
import { 
  RocketFilled, 
  SafetyCertificateFilled, 
  SelectOutlined,
  DotChartOutlined,
  SettingFilled,
  GlobalOutlined
} from '@ant-design/icons';
import SEO from '../components/SEO';

const { Title, Text, Paragraph } = Typography;

const Features = () => {
  return (
    <div className="w-full max-w-6xl mx-auto px-6 py-12 md:py-24 animate-fade-in-up">
      <SEO 
        title="Features - High-Performance Image Optimization"
        description="Discover why COMPRESS AI is the choice for thousands of users: ZIP batch processing, real-time telemetry, and enterprise-grade security."
        keywords={['image compression features', 'batch zip support', 'real-time compression metrics', 'lossless optimization benefits']}
      />
      
      <div className="text-center mb-16">
        <Title level={1} className="!text-white !text-5xl md:!text-6xl !font-bold tracking-tight mb-4 text-glow flex items-center justify-center gap-4">
          <RocketFilled className="text-purple-400" />
          FEATURES
        </Title>
        <Text className="text-purple-200/60 text-lg md:text-xl font-medium tracking-wide max-w-2xl block mx-auto">
          Built for the modern web. Engineered for speed, stability, and scale.
        </Text>
      </div>

      <Row gutter={[32, 32]}>
        <Col xs={24} md={12} lg={8}>
          <Card className="glass-panel-dark border-white/5 h-full hover:border-purple-500/30 transition-all duration-500 group relative">
            <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-purple-500/20 transition-all duration-300">
              <RocketFilled className="text-purple-400 text-2xl" />
            </div>
            <Title level={4} className="!text-white !font-bold mb-3">Instant Compression</Title>
            <Paragraph className="text-slate-400 text-sm leading-relaxed">
              Our automated workflow processes images in milliseconds using advanced COMPRESSAI Engine algorithms.
            </Paragraph>
          </Card>
        </Col>

        <Col xs={24} md={12} lg={8}>
          <Card className="glass-panel-dark border-white/5 h-full hover:border-indigo-500/30 transition-all duration-500 group relative">
            <div className="w-12 h-12 bg-indigo-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-indigo-500/20 transition-all duration-300">
              <SelectOutlined className="text-indigo-400 text-2xl" />
            </div>
            <Title level={4} className="!text-white !font-bold mb-3">Bulk ZIP Archive</Title>
            <Paragraph className="text-slate-400 text-sm leading-relaxed">
              Why upload one by one? Drag and drop entire ZIP archives up to 200MB for batch optimization workflows.
            </Paragraph>
          </Card>
        </Col>

        <Col xs={24} md={12} lg={8}>
          <Card className="glass-panel-dark border-white/5 h-full hover:border-emerald-500/30 transition-all duration-500 group relative">
            <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-emerald-500/20 transition-all duration-300">
              <DotChartOutlined className="text-emerald-400 text-2xl" />
            </div>
            <Title level={4} className="!text-white !font-bold mb-3">Real-Time Telemetry</Title>
            <Paragraph className="text-slate-400 text-sm leading-relaxed">
              Gain deep insight into every byte saved with our live telemetry data and efficiency metrics.
            </Paragraph>
          </Card>
        </Col>

        <Col xs={24} md={12} lg={8}>
          <Card className="glass-panel-dark border-white/5 h-full hover:border-red-500/30 transition-all duration-500 group relative">
            <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-red-500/20 transition-all duration-300">
              <SafetyCertificateFilled className="text-red-400 text-2xl" />
            </div>
            <Title level={4} className="!text-white !font-bold mb-3">Enterprise Grade Security</Title>
            <Paragraph className="text-slate-400 text-sm leading-relaxed">
              Secure login and encrypted processing ensure your high-value creative assets are always protected.
            </Paragraph>
          </Card>
        </Col>

        <Col xs={24} md={12} lg={8}>
          <Card className="glass-panel-dark border-white/5 h-full hover:border-blue-500/30 transition-all duration-500 group relative">
            <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-blue-500/20 transition-all duration-300">
              <SettingFilled className="text-blue-400 text-2xl" />
            </div>
            <Title level={4} className="!text-white !font-bold mb-3">Precision Engineering</Title>
            <Paragraph className="text-slate-400 text-sm leading-relaxed">
              Every feature is built with extreme precision for high-performance creative agency workflows.
            </Paragraph>
          </Card>
        </Col>

        <Col xs={24} md={12} lg={8}>
          <Card className="glass-panel-dark border-white/5 h-full hover:border-amber-500/30 transition-all duration-500 group relative">
            <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-amber-500/20 transition-all duration-300">
              <GlobalOutlined className="text-amber-400 text-2xl" />
            </div>
            <Title level={4} className="!text-white !font-bold mb-3">Modern Infrastructure</Title>
            <Paragraph className="text-slate-400 text-sm leading-relaxed">
              Cloud-native architecture ensures 99.9% uptime and reliable processing during peak workloads.
            </Paragraph>
          </Card>
        </Col>
      </Row>

    </div>
  );
};

export default Features;
